﻿<template>
  <section class="small-12 medium-12 large-6 columns">
    <!-- /************* WHAT HAPPENED *************/ -->
    <h1 v-html="vueModel.ContentViewModel.Step2Content.StepTitle" />

    <!-- /************* DESCRIBE WHAT HAPPENED *************/ -->
    <article>
      <h3 v-html="vueModel.ContentViewModel.Step2Content.TitleClaimEventDescription" />

      <p
        v-if="showProductSpecificClaimEventDescriptionInstructions"
        data-test-product-specific-description-instructions
        v-html="
          vueModel.ContentViewModel.Step2Content
            .ProductSpecificClaimEventDescriptionInstructionsText
        "
      />
      <p
        data-test-generic-description-instructions
        v-html="vueModel.ContentViewModel.Step2Content.GenericClaimEventDescriptionInstructionsText"
      />

      <div class="control">
        <Field
          as="textarea"
          v-model="vueModel.CreateClaimEntity.ClaimEventDescription"
          :rules="'required'"
          cols="30"
          rows="3"
          name="describe-incident"
          :label="vueModel.ContentViewModel.Step2Content.ErrorMessageClaimEventDescription"
        />
        <ValidationError name="describe-incident" />
      </div>

      <!-- Foto Documentation -->
      <div
        v-if="showGenericPhotoUpload"
        data-test-upload-photo-section
      >
        <h3
          data-test-upload-photo-title
          v-html="vueModel.ContentViewModel.GeneralContent.TitleAttachPhotos"
        />

        <p
          data-test-upload-photo-text
          v-html="vueModel.ContentViewModel.GeneralContent.TextAttachPhotos"
        />

        <upload
          v-model="vueModel.files"
          :content="vueModel.ContentViewModel.Step4Shared"
          :title="vueModel.ContentViewModel.GeneralContent.SubtitleAttachPhotos"
        />
      </div>
    </article>

    <!-- /************* INJURED TRAVELLERS LIST *************/ -->
    <article v-if="isTravel">
      <h3 v-html="claimTypeText" />
      <!-- List -->
      <div class="control">
        <fieldset class="checkbox-inline">
          <div v-for="(injuredTraveller, index) in filteredInjuredTravellers" :key="index">
            <input
              :id="'injured-traveller-' + index"
              v-model="injuredTraveller.IsInjured"
              type="checkbox"
              :disabled="!injuredTraveller.IsPolicyHolder"
            />
            <label :for="'injured-traveller-' + index">
              {{ injuredTraveller.FirstName }} {{ injuredTraveller.LastName }}</label>
            <span
              v-if="injuredTraveller.Editable"
              class="editable"
              @click="editTraveller(index, 'injured')"
            />
          </div>
          <div>
            <a class="plus-minus-link" @click="addTraveller('injured')">
              <span v-if="!toggleInjuredTravellerForm" class="plus" />
              <span v-if="toggleInjuredTravellerForm" class="minus" />
              {{ vueModel.ContentViewModel.Step2Content.ButtonTextAddInjuredTraveller }}
            </a>
          </div>
        </fieldset>
      </div>

      <!-- /************* INPUT INJURED TRAVELLER FIELDS *************/ -->
      <!-- Form -->
      <transition name="fade" mode="out-in">
        <div v-if="toggleInjuredTravellerForm" class="control">
          <div class="row">
            <!-- /*** INJURED TRAVELLER: FIRSTNAME ***/ -->
            <div class="small-12 medium-12 columns">
              <LabelAndTooltip
                for="injured-traveller-firstname"
                class="text-question"
                :label="vueModel.ContentViewModel.Step2Content.LabelInjuredTravellerFirstName"
                :tooltip="vueModel.ContentViewModel.Step2Content.TooltipInjuredTravellerFirstName"
              />
              <Field
                id="injured-traveller-firstname"
                v-model="traveller.FirstName"
                :rules="'required'"
                type="text"
                name="injured-traveller-firstname"
                :label="
                  vueModel.ContentViewModel.Step2Content.ErrorMessageInjuredTravellerFirstName
                "
                data-vv-scope="traveller"
              />
              <ValidationError name="injured-traveller-firstname" scope="traveller" />
            </div>
            <!-- /*** INJURED TRAVELLER: LASTNAME ***/ -->
            <div class="small-12 medium-12 columns">
              <LabelAndTooltip
                for="injured-traveller-lastname"
                class="text-question"
                :label="vueModel.ContentViewModel.Step2Content.LabelInjuredTravellerLastName"
                :tooltip="vueModel.ContentViewModel.Step2Content.TooltipInjuredTravellerLastName"
              />
              <Field
                id="injured-traveller-lastname"
                v-model="traveller.LastName"
                :rules="'required'"
                type="text"
                name="injured-traveller-lastname"
                :label="vueModel.ContentViewModel.Step2Content.ErrorMessageInjuredTravellerLastName"
                data-vv-scope="traveller"
              />
              <ValidationError name="injured-traveller-lastname" scope="traveller" />
            </div>
            <!-- /*** INJURED TRAVELLER: ADDRESS SEARCH ***/ -->
            <div class="small-12 medium-12 columns">
              <LabelAndTooltip
                for="injured-traveller-address"
                class="text-question"
                :label="vueModel.ContentViewModel.Step2Content.LabelInjuredTravellerAddressSearch"
                :tooltip="
                  vueModel.ContentViewModel.Step2Content.TooltipInjuredTravellerAddressSearch
                "
              />
              <Field
                v-slot="{ field, errors }"
                v-model="traveller.SelectedAddress"
                name="injured-traveller-address"
                :label="
                  vueModel.ContentViewModel.Step2Content.ErrorMessageInjuredTravellerAddressSearch
                "
                :rules="'required'"
              >
                <address-search
                  :name="field.name"
                  data-vv-scope="traveller"
                  class="v-select-search-icon"
                  :errors="errors"
                  :model-value="traveller.SelectedAddress"
                  @update:model-value="traveller.SelectedAddress = $event"
                />
                <ValidationError :name="field.name" scope="traveller" />
              </Field>
            </div>
            <!-- /*** INJURED TRAVELLER: CPR NUMBER ***/ -->
            <div class="small-12 medium-12 columns">
              <CprNumberField
                v-model="traveller.CprNumber"
                name="injured-traveller-cpr"
                scope="traveller"
                @update:model-value="updateCprProps($event)"
                :model-value="traveller.CprNumber"
                :label="vueModel.ContentViewModel.Step2Content.LabelInjuredTravellerCprNumber"
                :cpr-label="vueModel.ContentViewModel.Step2Content.LabelInjuredTravellerCprNumber"
                :cpr-tooltip="
                  vueModel.ContentViewModel.Step2Content.TooltipInjuredTravellerCprNumber
                "
              />
            </div>
            <div class="small-12 medium-6 columns">
              <button
                v-if="editIndex > 0"
                class="button secondary"
                @click="deleteTraveller"
                v-html="vueModel.ContentViewModel.Step2Content.ButtonTextDeleteInjuredTraveller"
              />
            </div>
            <div class="small-12 medium-6 columns">
              <button
                class="button"
                data-test="save-injured-traveller-button"
                @click="saveTraveller('injured')"
                v-html="vueModel.ContentViewModel.Step2Content.ButtonTextSaveInjuredTraveller"
              />
            </div>
          </div>
        </div>
      </transition>
    </article>

    <!-- /************* ADDITIONAL TRAVELLERS LIST *************/ -->
    <article v-if="isTravel">
      <h3 v-html="vueModel.ContentViewModel.Step2Content.AdditionalTravellerHeadline" />
      <!-- List -->
      <div class="control">
        <fieldset class="checkbox-inline">
          <div v-for="(additionalTraveller, index) in filteredAdditionalTravellers" :key="index">
            <input
              :id="'additional-traveller-' + index"
              v-model="additionalTraveller.IsAdditionalTraveller"
              type="checkbox"
              :disabled="!additionalTraveller.IsPolicyHolder"
            />
            <label :for="'additional-traveller-' + index">
              {{ additionalTraveller.FirstName }} {{ additionalTraveller.LastName }}</label>
            <span
              v-if="additionalTraveller.Editable"
              class="editable"
              @click="editTraveller(index, 'additional')"
            />
          </div>
          <div>
            <a class="plus-minus-link" @click="addTraveller('additional')">
              <span v-if="!toggleAdditionalTravellerForm" class="plus" />
              <span v-if="toggleAdditionalTravellerForm" class="minus" />
              {{ vueModel.ContentViewModel.Step2Content.ButtonTextAddAdditionalTraveller }}
            </a>
          </div>
        </fieldset>
      </div>
      <!-- /************* INPUT ADDITIONAL TRAVELLER FIELDS *************/ -->
      <!-- Form -->
      <transition name="fade" mode="out-in">
        <div v-if="toggleAdditionalTravellerForm" class="control">
          <div class="row">
            <!-- /*** ADDITIONAL TRAVELLER: FIRSTNAME ***/ -->
            <div class="small-12 medium-12 columns">
              <LabelAndTooltip
                for="additional-traveller-firstname"
                class="text-question"
                :label="vueModel.ContentViewModel.Step2Content.LabelAdditionalTravellerFirstName"
                :tooltip="
                  vueModel.ContentViewModel.Step2Content.TooltipAdditionalTravellerFirstName
                "
              />
              <Field
                id="additional-traveller-firstname"
                v-model="traveller.FirstName"
                :rules="'required'"
                type="text"
                name="additional-traveller-firstname"
                :label="
                  vueModel.ContentViewModel.Step2Content.ErrorMessageAdditionalTravellerFirstName
                "
                data-vv-scope="traveller"
              />
              <ValidationError name="additional-traveller-firstname" scope="traveller" />
            </div>
            <!-- /*** ADDITIONAL TRAVELLER: LASTNAME ***/ -->
            <div class="small-12 medium-12 columns">
              <LabelAndTooltip
                for="additional-traveller-lastname"
                class="text-question"
                :label="vueModel.ContentViewModel.Step2Content.LabelAdditionalTravellerLastName"
                :tooltip="vueModel.ContentViewModel.Step2Content.TooltipAdditionalTravellerLastName"
              />
              <Field
                id="additional-traveller-lastname"
                v-model="traveller.LastName"
                :rules="'required'"
                type="text"
                name="additional-traveller-lastname"
                :label="
                  vueModel.ContentViewModel.Step2Content.ErrorMessageAdditionalTravellerLastName
                "
                data-vv-scope="traveller"
              />
              <ValidationError name="additional-traveller-lastname" scope="traveller" />
            </div>
            <!-- /*** ADDITIONAL TRAVELLER: ADDRESS SEARCH ***/ -->
            <div class="small-12 medium-12 columns">
              <LabelAndTooltip
                for="additional-traveller-address"
                class="text-question"
                :label="
                  vueModel.ContentViewModel.Step2Content.LabelAdditionalTravellerAddressSearch
                "
                :tooltip="
                  vueModel.ContentViewModel.Step2Content.TooltipAdditionalTravellerAddressSearch
                "
              />
              <Field
                v-slot="{ field, errors }"
                v-model="traveller.SelectedAddress"
                name="additional-traveller-address"
                :label="
                  vueModel.ContentViewModel.Step2Content
                    .ErrorMessageAdditionalTravellerAddressSearch
                "
                :rules="'required'"
              >
                <address-search
                  :name="field.name"
                  data-vv-scope="traveller"
                  class="v-select-search-icon"
                  :errors="errors"
                  :model-value="traveller.SelectedAddress"
                  @update:model-value="traveller.SelectedAddress = $event"
                />
                <ValidationError :name="field.name" scope="traveller" />
              </Field>
            </div>
            <!-- /*** ADDITIONAL TRAVELLER: CPR NUMBER***/ -->
            <div class="small-8 medium-8 columns">
              <LabelAndTooltip
                for="cpr"
                class="text-question"
                :label="vueModel.ContentViewModel.Step2Content.LabelAdditionalTravellerCprNumber"
                :tooltip="
                  vueModel.ContentViewModel.Step2Content.TooltipAdditionalTravellerCprNumber
                "
              />
              <Field
                id="additional-traveller-birthdate"
                v-model="traveller.BirthDay"
                :rules="'required|min:6|numeric'"
                type="text"
                name="additional-traveller-birthdate"
                :label="
                  vueModel.ContentViewModel.Step2Content.ErrorMessageAdditionalTravellerCprNumber
                "
                data-vv-scope="traveller"
                maxlength="6"
              />
              <ValidationError name="additional-traveller-birthdate" scope="traveller" />
            </div>
            <div class="small-12 medium-6 columns">
              <button
                v-if="editIndex > 0"
                class="button secondary"
                @click="deleteTraveller"
                v-html="vueModel.ContentViewModel.Step2Content.ButtonTextDeleteAdditionalTraveller"
              />
            </div>
            <div class="small-12 medium-6 columns">
              <button
                class="button"
                data-test="save-additional-traveller-button"
                @click="saveTraveller('additional')"
                v-html="vueModel.ContentViewModel.Step2Content.ButtonTextSaveAdditionalTraveller"
              />
            </div>
          </div>
        </div>
      </transition>
    </article>

    <!------------- Questionnaire ------------------------------------------------------------->
    <article v-if="hasQuestionnaireQuestions">
      <!--<h3>Vælg skadetype for indboskade</h3>-->

      <questionnaire :options="questions" />
    </article>

    <!------------- Kontakoplysninger på skadelidte ----------------------------------------->
    <article v-if="stepModel.ProductSpecific.AskForInsuredCounterParty">
      <injured-counterparty
        id="injured-counter-party"
        v-model="vueModel.CreateClaimEntity.ProductSpecific.InjuredCounterParty"
        :content="vueModel.ContentViewModel.Step2Content"
        :step-model="stepModel"
        data-test-injured-counter-party
        :errors="errors"
      />
    </article>

    <!------------- Er tyveriet anmeldt til politiet? ----------------------------------------->
    <article v-if="stepModel.ProductSpecific.AskForPoliceReportFiled" id="police-report-filed">
      <h3 v-html="vueModel.ContentViewModel.Step2Content.TitlePoliceReport" />

      <div class="control">
        <fieldset>
          <div class="row">
            <div
              v-for="(item, key) in stepModel.ProductSpecific.PoliceReportFiledOptions"
              :key="key"
              class="small-12 medium-6 columns radio-stacked"
            >
              <input
                :id="'police-report-filed-' + item.Value"
                v-model="stepModel.ProductSpecific.PoliceReportFiled"
                :rules="'required'"
                :value="item.Value"
                name="police-report-filed"
                type="radio"
              />
              <label :for="'police-report-filed-' + item.Value">
                {{ item.Label }}
              </label>
            </div>
          </div>

          <div
            v-if="stepModel.ProductSpecific.ShowBikeInformation"
            class="info-box info-box-align-left is-shown push-down"
          >
            <p>
              Hvis du kender cyklens stelnummer, skal cykeltyveriet anmeldes til politiet så cyklens
              stelnummer fremgår af politiets cykelregister.
            </p>
          </div>
        </fieldset>

        <ValidationError
          name="police-report-filed"
          :overrides="{
            required: vueModel.ContentViewModel.Step2Content.ErrorMessagePoliceReport
          }"
        />
      </div>

      <div
        v-if="
          stepModel.ProductSpecific.PoliceReportFiled &&
          stepModel.ProductSpecific.AskForPoliceInformation
        "
      >
        <police-information
          v-model="vueModel.CreateClaimEntity.ProductSpecific.PoliceInformation"
          :content-model="vueModel.ContentViewModel.Step2Content"
          :required="stepModel.ProductSpecific.PoliceReportFiled"
          :claim-event-date="vueModel.CreateClaimStep1Model.ClaimEventDate"
        >
          <template #tooltip1>
            <span v-html="vueModel.ContentViewModel.Step2Content.LabelAndTooltipPoliceReportWhen" />
          </template>

          <template #tooltip2>
            <span
              v-html="
                vueModel.ContentViewModel.Step2Content.LabelAndTooltipPoliceReportJournalNumber
              "
            />
          </template>

          <template #tooltip3>
            <span
              v-html="vueModel.ContentViewModel.Step2Content.LabelAndTooltipPoliceReportDistrict"
            />
          </template>
        </police-information>
      </div>
    </article>
  </section>
</template>

<script>
import ValidationError from '@/components/UI/ValidationError.vue';
import Upload from '../../components/Upload.vue';
import InjuredCounterparty from '../../components/InjuredCounterparty.vue';
import ReportClaimMixin from '@/components/Feature/Claims/flows/ReportClaimMixin.vue';
import ReportClaimServicesMixin from '@/components/Feature/Claims/flows/ReportClaimServicesMixin.vue';

// import { defineComponent } from 'vue';

export default {
  name: 'ReportClaimContentStep2',
  components: { ValidationError, Upload, InjuredCounterparty },
  mixins: [ReportClaimMixin, ReportClaimServicesMixin],
  props: {
    vueModel: {
      type: Object,
      default() {
        return {};
      }
    },
    errors: {
      type: Object,
      default() {
        return null;
      }
    }
  },
  data() {
    return {
      stepModel: this.vueModel.CreateClaimStep2Model,
      toggleInjuredTravellerForm: false,
      toggleAdditionalTravellerForm: false,
      traveller: null,
      toggleDeleteButton: false,
      editIndex: 0,
      isTravel: false,
      isInjuredTraveller: true,
      isAdditionalTraveller: true,
      showGenericPhotoUpload: !this.vueModel.CreateClaimStep2Model.HideGenericPhotoUpload,
    };
  },
  computed: {
    numberOfTravellers() {
      return this.vueModel.CreateClaimEntity.ProductSpecific.Travellers.length;
    },
    filteredInjuredTravellers() {
      return this.vueModel.CreateClaimEntity.ProductSpecific.Travellers.filter(
        (traveller) => traveller.IsInjured || traveller.IsPolicyHolder
      );
    },
    filteredAdditionalTravellers() {
      return this.vueModel.CreateClaimEntity.ProductSpecific.Travellers.filter(
        (traveller) => traveller.IsAdditionalTraveller || traveller.IsPolicyHolder
      );
    },
    claimTypeText() {
      let str = '';
      const claimType = this.vueModel.CreateClaimStep2Model.DamageCodeForTitleSelection;

      switch (claimType) {
        case 10:
        case 70:
        case 71:
          str = this.vueModel.ContentViewModel.Step2Content.InjuredTravellerHeadlineAfbestilling;
          break;
        case 13:
          str = this.vueModel.ContentViewModel.Step2Content.InjuredTravellerHeadlineDodsfald;
          break;
        case 17:
          str = this.vueModel.ContentViewModel.Step2Content.InjuredTravellerHeadlineErstatningsbil;
          break;
        case 19:
          str = this.vueModel.ContentViewModel.Step2Content.InjuredTravellerHeadlineEvakuering;
          break;
        case 14:
          str = this.vueModel.ContentViewModel.Step2Content.InjuredTravellerHeadlineForsinkelse;
          break;
        case 15:
          str = this.vueModel.ContentViewModel.Step2Content.InjuredTravellerHeadlineHjemkaldelse;
          break;
        case 16:
          str = this.vueModel.ContentViewModel.Step2Content.InjuredTravellerHeadlineRejsedokumenter;
          break;
        case 20:
          str =
            this.vueModel.ContentViewModel.Step2Content.InjuredTravellerHeadlineSelvrisikoLejebil;
          break;
        case 18:
          str = this.vueModel.ContentViewModel.Step2Content.InjuredTravellerHeadlineSkadeFeriebolig;
          break;
        case 11:
          str = this.vueModel.ContentViewModel.Step2Content.InjuredTravellerHeadlineSygdom;
          break;
        case 12:
          str = this.vueModel.ContentViewModel.Step2Content.InjuredTravellerHeadlineTilskadekomst;
          break;
        default:
          str = 'Something here in case of travel';
          break;
      }

      return str;
    }
  },
  mounted() {
    this.isTravel = this.stepModel.ProductSpecific.IsTravel;

    if (
      this.vueModel.ContentViewModel.Step2Content
        .ProductSpecificClaimEventDescriptionInstructionsText
    ) {
      this.ProductSpecificClaimEventDescriptionInstructions =
        this.vueModel.ContentViewModel.Step2Content.ProductSpecificClaimEventDescriptionInstructionsText;
    }

    if (!this.isTravel) {
      return;
    }

    // add Editable property
    for (var i in this.vueModel.CreateClaimEntity.ProductSpecific.Travellers) {
      if (this.vueModel.CreateClaimEntity.ProductSpecific.Travellers[i].Editable === undefined) {
        this.vueModel.CreateClaimEntity.ProductSpecific.Travellers[i].Editable = false;
      }
    }
  },
  methods: {
    showProductSpecificClaimEventDescriptionInstructions() {
      if (
        ProductSpecificClaimEventDescriptionInstructions !== '' ||
        (typeof ProductSpecificClaimEventDescriptionInstructions === 'string' &&
          ProductSpecificClaimEventDescriptionInstructions.trim().length() !== 0)
      ) {
        return true;
      }

      return false;
    },
    addTraveller(type) {
      this.reset();

      if (type === 'injured') {
        this.toggleInjuredTravellerForm = !this.toggleInjuredTravellerForm;
        this.toggleAdditionalTravellerForm = false;
      } else if (type === 'additional') {
        this.toggleInjuredTravellerForm = false;
        this.toggleAdditionalTravellerForm = !this.toggleAdditionalTravellerForm;
      }

      // prefill, testing only
      // this.traveller.FirstName = 'Nyt';
      // this.traveller.LastName = 'Navn';
      // this.traveller.BirthDay = '123456';
      // this.traveller.LastFourDigitsAfCpr = '1234';
    },
    editTraveller(index, type) {
      // set editIndex so saveTraveller() knows which index to update
      this.editIndex = index;

      if (type === 'injured') {
        this.resetTraveller();
        this.toggleAdditionalTravellerForm = false;

        if (this.editIndex === 0) {
          this.toggleInjuredTravellerForm = !this.toggleInjuredTravellerForm;
        } else {
          this.toggleInjuredTravellerForm = true;
        }

        // populate props
        this.traveller.LastFourDigitsAfCpr =
          this.vueModel.CreateClaimEntity.ProductSpecific.Travellers[index].LastFourDigitsAfCpr;
      } else if (type === 'additional') {
        this.resetTraveller();
        this.toggleInjuredTravellerForm = false;

        if (this.editIndex === 0) {
          this.toggleAdditionalTravellerForm = !this.toggleAdditionalTravellerForm;
        } else {
          this.toggleAdditionalTravellerForm = true;
        }
      }

      // populate props
      this.traveller.FirstName =
        this.vueModel.CreateClaimEntity.ProductSpecific.Travellers[index].FirstName;
      this.traveller.LastName =
        this.vueModel.CreateClaimEntity.ProductSpecific.Travellers[index].LastName;
      this.traveller.SelectedAddress =
        this.vueModel.CreateClaimEntity.ProductSpecific.Travellers[index].SelectedAddress;
      this.traveller.BirthDay =
        this.vueModel.CreateClaimEntity.ProductSpecific.Travellers[index].BirthDay;
      this.traveller.CprNumber = this.traveller.BirthDay + this.traveller.LastFourDigitsAfCpr;
      this.traveller.IsInjured =
        this.vueModel.CreateClaimEntity.ProductSpecific.Travellers[index].IsInjured;
      this.traveller.IsAdditionalTraveller =
        this.vueModel.CreateClaimEntity.ProductSpecific.Travellers[index].IsAdditionalTraveller;
    },
    async saveTraveller(type) {
      let fields = [];
      if (type === 'injured') {
        fields = [
          'injured-traveller-address',
          'injured-traveller-firstname',
          'injured-traveller-lastname',
          'cprinjured-traveller-cpr'
        ];
      } else if (type === 'additional') {
        fields = [
          'additional-traveller-address',
          'additional-traveller-firstname',
          'additional-traveller-lastname',
          'additional-traveller-birthdate'
        ];
      } else throw 'Cannot find type: ' + type;

      let form = this.$parent.$parent;

      for (var field in fields) await form.validateField(fields[field]);

      for (var field in fields) {
        if (form.getErrors()[fields[field]] != undefined) {
          return;
        }
      }

      this.updateAddress(this.traveller.Address, this.traveller.SelectedAddress);

      const insertIndex = this.editIndex !== 0 ? this.editIndex : this.numberOfTravellers;
      const deleteExisting = this.editIndex !== 0 ? 1 : 0;
      // console.log('inserting into index', insertIndex);
      // console.log('delete existing', deleteExisting);
      this.vueModel.CreateClaimEntity.ProductSpecific.Travellers.splice(
        insertIndex,
        deleteExisting,
        this.traveller
      );

      if (type === 'injured') {
        this.traveller.IsInjured = true;
        this.toggleInjuredTravellerForm = false;
      } else if (type === 'additional') {
        this.traveller.IsAdditionalTraveller = true;
        this.toggleAdditionalTravellerForm = false;
      }

      this.reset();
    },
    deleteTraveller() {
      // console.log('delete', this.editIndex);
      this.vueModel.CreateClaimEntity.ProductSpecific.Travellers.splice(this.editIndex, 1);
      this.toggleInjuredTravellerForm = false;
      this.toggleAdditionalTravellerForm = false;
      this.reset();
    },
    resetTraveller() {
      this.traveller = {
        FirstName: null,
        LastName: null,
        SelectedAddress: null,
        Address: {
          Street: null,
          HouseNumber: null,
          Floor: null,
          Designation: null,
          District: null,
          PostalCode: null,
          City: null,
          CountryRef: 'DK'
        },
        Phone: null,
        Email: null,
        BirthDay: null,
        LastFourDigitsAfCpr: null,
        CprNumber: null,
        ContactId: null,
        IsInjured: false,
        IsAdditionalTraveller: false,
        Editable: true,
        IsPolicyHolder: false
      };
    },
    reset() {
      this.resetTraveller();
      this.editIndex = 0;
    },
    updateCprProps(params) {
      this.traveller.BirthDay = params.substring(0, 6);
      this.traveller.LastFourDigitsAfCpr = params.substring(6, 10);
    }
  }
};
</script>
